import type { LeaseCompanyMst } from '@libs/domain';
import { leaseCompanyMst } from '@libs/domain';
import { eligibleBillingProvider } from '@libs/enums';
import type { MstRangeSearchProps, MstSearchDialogProps, MstSearchProps } from '@libs/templates/mst';
import { MstSearchDialogTmpl } from '@libs/templates/mst';
import type { Characters, Item, PageProps, WithExclusion } from '@ps-smart-cp/ppfw-client';
import { MasterRangeSearchField, MasterSearchField, withRerender } from '@ps-smart-cp/ppfw-client';
import { AdditionalSearchBasicInfo, leaseCompanyMstColumns } from './components';
import { leaseCompanyMstListSearchFieldConfig } from './item-config';
import type { LeaseCompanyMstRegistration } from './models';
import { LeaseCompanyMstListSearch } from './models';
import { fetchMst, listRequest } from './repositories';

function InnerComponent(props: MstSearchDialogProps<LeaseCompanyMstRegistration> & PageProps) {
  return (
    <MstSearchDialogTmpl
      mstName={leaseCompanyMst.names.laCompany}
      columns={leaseCompanyMstColumns}
      items={leaseCompanyMstListSearchFieldConfig}
      parsers={{ eligibleBillingProviderCondition: eligibleBillingProvider.toDisplay }}
      listRequestFunction={listRequest}
      initMst={LeaseCompanyMstListSearch.init}
      {...props}
    >
      {AdditionalSearchBasicInfo}
    </MstSearchDialogTmpl>
  );
}

const Dialog = withRerender(InnerComponent, 'LeaseCompanyMstDialog');

function LeaseCompanyMasterSearch<
  T extends Pick<LeaseCompanyMst['schema'], K>,
  K extends 'laCompanyAfterChange' | 'laCompanyMstCode',
  I extends Record<K, Item<Characters>>,
>(props: MstSearchProps<T, K, I>) {
  return (
    <MasterSearchField getMaster={async (code) => fetchMst({ code })} {...props}>
      {(setIsOpen, setMaster, getMst) => (
        <Dialog setIsOpen={setIsOpen} setMaster={setMaster} getMaster={getMst} />
      )}
    </MasterSearchField>
  );
}

function LeaseCompanyMasterRangeSearch<
  T extends Pick<LeaseCompanyMst['schema'], K>,
  K extends 'laCompanyCodeRange' | 'laCompanyMstCodeRange',
  I extends Record<K, Item<WithExclusion>>,
>(props: MstRangeSearchProps<T, K, I>) {
  return (
    <MasterRangeSearchField getMaster={async (code) => fetchMst({ code })} {...props}>
      {(setIsOpen, setMaster, getMst) => (
        <Dialog setIsOpen={setIsOpen} setMaster={setMaster} getMaster={getMst} />
      )}
    </MasterRangeSearchField>
  );
}

export { LeaseCompanyMasterRangeSearch, LeaseCompanyMasterSearch };
